import * as React from 'react';
import Box from '@mui/material/Box';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { DeleteStatusResponse } from '../generated/api-client';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowModel,
  GridRowsProp,
} from '@mui/x-data-grid';
import { itIT } from '@mui/x-data-grid/locales';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

import { PageLayout } from './layout';
import { ActionButton } from './common';
import { ConfirmDialog } from './common/ConfirmDialog';
import { formatNumberItalian, formatNumberItalianSignificant } from '../utils/numberFormat';
import { parseDateForGrid } from '../utils/date.utils';
import { convertToCSV, downloadCSV } from '../utils/csvExport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

export interface CrudField {
  field: string;
  headerName: string;
  width?: number;
  type?: 'string' | 'number' | 'date' | 'singleSelect' | 'boolean';
  editable?: boolean;
  valueOptions?:
    | string[]
    | {
        value: string;
        label: string;
      }[];
  align?: 'left' | 'right' | 'center';
  headerAlign?: 'left' | 'right' | 'center';
  renderEditCell?: (params: any) => React.ReactNode;
  valueFormatter?: (value: any) => string;
  hide?: boolean;
  /** Numero di decimali per colonne number (politica formattazione). */
  decimalPlaces?: number;
  /** Cifre significative (es. Indicatori: 3). Ha precedenza su decimalPlaces se impostato. */
  significantDigits?: number;
}

export interface CustomAction {
  label: string;
  icon: React.ReactElement;
  onClick: (id: GridRowId, row: any) => void;
  color?: 'inherit' | 'primary' | 'default';
}

export interface CrudConfig<TRead, TCreate> {
  queryKey: string;
  fields: CrudField[];
  listFn: () => Promise<TRead[]>;
  createFn: (data: TCreate) => Promise<TRead>;
  updateFn: (id: string, data: TCreate) => Promise<TRead>;
  deleteFn: (id: string) => Promise<void | DeleteStatusResponse>;
  getId: (row: TRead) => string;
  getCreateDto: (row: GridRowModel) => TCreate;
  customActions?: CustomAction[];
  getDefaultValues?: () => Partial<GridRowModel> | undefined;
}

export interface CrudDataGridProps<TRead, TCreate> {
  config: CrudConfig<TRead, TCreate>;
  height?: string | number;
}

export interface CrudDataGridHandle {
  addRow: () => void;
}

export const CrudDataGrid = React.forwardRef<CrudDataGridHandle, CrudDataGridProps<any, any>>(
  ({ config, height = 'calc(100vh - 185px)' }, ref) => {
    const queryClient = useQueryClient();
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [localRows, setLocalRows] = React.useState<GridRowsProp>([]);
    const [contextMenu, setContextMenu] = React.useState<{
      mouseX: number;
      mouseY: number;
    } | null>(null);
    const gridRef = React.useRef<HTMLDivElement>(null);

    const { data: rows = [], isLoading, error } = useQuery(config.queryKey, config.listFn);

    const createMutation = useMutation(config.createFn, {
      onSuccess: () => {
        queryClient.invalidateQueries(config.queryKey);
        toast.success('Record creato con successo');
      },
      onError: (error: any) => {
        if (Array.isArray(error?.detail)) {
          for (const detail of error.detail) {
            toast.error(`${detail.msg} (${detail.loc[1]})`);
          }
        } else {
          toast.error('Creazione del record non riuscita');
        }
      },
    });

    const updateMutation = useMutation(
      ({ id, data }: { id: string; data: any }) => config.updateFn(id, data),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(config.queryKey);
          toast.success('Record aggiornato con successo');
        },
        onError: (error: any) => {
          if (Array.isArray(error?.detail)) {
            for (const detail of error.detail) {
              toast.error(`${detail.msg} (${detail.loc[1]})`);
            }
          } else {
            toast.error('Aggiornamento del record non riuscito');
          }
        },
      }
    );

    const deleteMutation = useMutation(config.deleteFn, {
      onSuccess: () => {
        queryClient.invalidateQueries(config.queryKey);
        toast.success('Record eliminato con successo');
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Eliminazione del record non riuscita');
      },
    });

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
      if (params.reason === GridRowEditStopReasons.rowFocusOut) {
        event.defaultMuiPrevented = true;
      }
    };

    const handleEditClick = (id: GridRowId) => () => {
      setRowModesModel(old => ({ ...old, [id]: { mode: GridRowModes.Edit } }));
    };

    const handleSaveClick = (id: GridRowId) => () => {
      setRowModesModel(old => ({ ...old, [id]: { mode: GridRowModes.View } }));
    };

    const [deleteDialogState, setDeleteDialogState] = React.useState<{
      open: boolean;
      id: GridRowId | null;
    }>({ open: false, id: null });

    const handleDeleteClick = (id: GridRowId) => () => {
      setDeleteDialogState({ open: true, id });
    };

    const handleCloseDeleteDialog = () => {
      setDeleteDialogState({ open: false, id: null });
    };

    const handleConfirmDelete = () => {
      if (deleteDialogState.id != null) {
        deleteMutation.mutate(deleteDialogState.id as string);
      }
      setDeleteDialogState({ open: false, id: null });
    };

    const handleCancelClick = (id: GridRowId) => () => {
      setRowModesModel(old => ({
        ...old,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
      setLocalRows(oldRows => oldRows.filter(row => row.id !== id));
    };

    const handleAddClick = React.useCallback(() => {
      const newId = `new-${Date.now()}`;
      const newRow: any = { id: newId, isNew: true };
      config.fields
        .filter(field => field.field !== 'id')
        .forEach(field => {
          newRow[field.field] = null;
        });
      if (config.getDefaultValues) {
        Object.assign(newRow, config.getDefaultValues() ?? {});
      }
      setLocalRows(oldRows => [...oldRows, newRow]);
      setRowModesModel(oldModel => ({
        ...oldModel,
        [newId]: { mode: GridRowModes.Edit, fieldToFocus: config.fields[0]?.field },
      }));
    }, [config.fields]);

    React.useImperativeHandle(
      ref,
      () => ({
        addRow: handleAddClick,
      }),
      [handleAddClick]
    );

    const normalizeRowDates = React.useCallback(
      (row: Record<string, unknown>) => {
        const normalized: Record<string, unknown> = { ...row };
        config.fields.forEach(field => {
          if (field.type === 'date') {
            normalized[field.field] = parseDateForGrid(
              row[field.field] as Date | string | null | undefined
            );
          }
        });
        return normalized;
      },
      [config.fields]
    );

    const processRowUpdate = async (newRow: GridRowModel) => {
      const dto = config.getCreateDto(newRow);
      if (newRow.isNew) {
        const created = await createMutation.mutateAsync(dto);
        const temporaryId = newRow.id as string;
        setLocalRows(oldRows => oldRows.filter(row => row.id !== temporaryId));
        return normalizeRowDates({ ...created, id: config.getId(created) });
      }

      await updateMutation.mutateAsync({ id: newRow.id as string, data: dto });
      return { ...normalizeRowDates(newRow), isNew: false };
    };

    const handleRowModesModelChange = (newModel: GridRowModesModel) => {
      setRowModesModel(newModel);
    };

    const gridRows: GridRowsProp = React.useMemo(() => {
      const converted = rows.map(row => {
        const gridRow: any = { id: config.getId(row) };
        config.fields.forEach(field => {
          const raw = (row as any)[field.field] ?? null;
          gridRow[field.field] =
            field.type === 'date' ? parseDateForGrid(raw) : raw;
        });
        return gridRow;
      });

      const localIds = new Set(localRows.map(row => row.id));
      const serverRows = converted.filter(row => !localIds.has(row.id));
      return [...localRows, ...serverRows];
    }, [rows, localRows, config.fields, config]);

    // Funzione per esportare la tabella in CSV
    const handleExportCSV = React.useCallback(() => {
      const csvContent = convertToCSV(Array.from(gridRows), config.fields);
      downloadCSV(csvContent, 'export');
      setContextMenu(null);
    }, [gridRows, config.fields]);

    // Gestore per il click destro sulla griglia
    React.useEffect(() => {
      const handleContextMenu = (event: MouseEvent) => {
        if (!gridRef.current) return;

        const target = event.target as HTMLElement;
        const gridElement = target.closest('.MuiDataGrid-root');

        if (gridElement) {
          event.preventDefault();
          setContextMenu({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          });
        }
      };

      const gridElement = gridRef.current;
      if (gridElement) {
        gridElement.addEventListener('contextmenu', handleContextMenu);
        return () => {
          gridElement.removeEventListener('contextmenu', handleContextMenu);
        };
      }
    }, []);

    const columns: GridColDef[] = React.useMemo(() => {
      const baseColumns: GridColDef[] = config.fields.map(field => {
        const hasCustomFormatter = typeof field.valueFormatter === 'function';

        const valueFormatter = hasCustomFormatter
          ? (params: any) => field.valueFormatter?.(params?.value || params)
          : field.type === 'number'
            ? (value: any) => {
                if (field.significantDigits != null) {
                  return formatNumberItalianSignificant(value, field.significantDigits);
                }
                if (field.decimalPlaces != null) {
                  return formatNumberItalian(value, { maximumFractionDigits: field.decimalPlaces });
                }
                return formatNumberItalian(value as any);
              }
            : undefined;

        // Usa renderEditCell personalizzato se fornito
        const renderEditCell = field.renderEditCell;

        return {
          field: field.field,
          headerName: field.headerName,
          width: field.width ?? 150,
          type: field.type,
          editable: field.editable !== false,
          valueOptions: field.valueOptions,
          align: field.align,
          headerAlign: field.headerAlign,
          renderEditCell,
          valueFormatter,
          valueGetter:
            field.type === 'date'
              ? (value, row) =>
                  parseDateForGrid(row[field.field] as Date | string | null | undefined)
              : undefined,
          hide: field.hide,
        };
      });

      const actionsColumn: GridColDef = {
        field: 'actions',
        type: 'actions',
        headerName: 'Azioni',
        width: 100 + (config.customActions?.length ?? 0) * 40,
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
          if (isInEditMode) {
            return [
              <GridActionsCellItem
                key="save"
                icon={<SaveIcon />}
                label="Salva"
                onClick={handleSaveClick(id)}
                color="primary"
              />,
              <GridActionsCellItem
                key="cancel"
                icon={<CancelIcon />}
                label="Annulla"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          const actions: React.ReactElement[] = [
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label="Modifica"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Elimina"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];

          // Aggiungi azioni personalizzate se presenti
          if (config.customActions) {
            const row = rows.find(r => config.getId(r) === id);
            config.customActions.forEach((customAction, index) => {
              actions.push(
                <GridActionsCellItem
                  key={`custom-${index}`}
                  icon={customAction.icon}
                  label={customAction.label}
                  onClick={() => customAction.onClick(id, row)}
                  color={customAction.color || 'inherit'}
                />
              );
            });
          }

          return actions;
        },
      };

      return [...baseColumns, actionsColumn];
    }, [config.fields, rowModesModel, config.customActions, rows, config]);

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
          <Typography>Caricamento...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">Errore nel caricamento dei dati.</Typography>
        </Box>
      );
    }

    return (
      <>
        <Box ref={gridRef} sx={{ height }}>
          <DataGrid
            columnHeaderHeight={36}
            sx={{ borderRadius: 2 }}
            rows={gridRows}
            columns={columns}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            disableRowSelectionOnClick
            checkboxSelection={false}
            getRowHeight={() => 36}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          />
        </Box>

        <Menu
          open={contextMenu !== null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
        >
          <MenuItem onClick={handleExportCSV}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Esporta tabella in CSV</ListItemText>
          </MenuItem>
        </Menu>

        <ConfirmDialog
          open={deleteDialogState.open}
          loading={deleteMutation.isLoading}
          onCancel={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
        />
      </>
    );
  }
);

CrudDataGrid.displayName = 'CrudDataGrid';

interface CrudDataGridPageProps<TRead, TCreate> extends CrudDataGridProps<TRead, TCreate> {
  title: string;
  icon?: React.ReactElement;
  actions?: React.ReactNode;
}

export const CrudDataGridPage = React.forwardRef<
  CrudDataGridHandle,
  CrudDataGridPageProps<any, any>
>(({ title, icon = <SettingsIcon fontSize="large" />, actions, ...gridProps }, ref) => {
  const internalRef = React.useRef<CrudDataGridHandle>(null);

  React.useImperativeHandle(ref, () => ({
    addRow: () => internalRef.current?.addRow(),
  }));

  const defaultActions = (
    <ActionButton
      icon={<AddIcon />}
      tooltip="Aggiungi record"
      onClick={() => internalRef.current?.addRow()}
    />
  );

  return (
    <PageLayout
      header={
        <>
          {icon}
          <Typography variant="h5" sx={{ marginLeft: '1rem' }}>
            {title}
          </Typography>
        </>
      }
      actions={actions ?? defaultActions}
    >
      <Box sx={{ p: 2 }}>
        <CrudDataGrid ref={internalRef} {...gridProps} />
      </Box>
    </PageLayout>
  );
});

CrudDataGridPage.displayName = 'CrudDataGridPage';
